import { useEffect, useState } from 'react';
import { api, type Incident } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatRelative } from '@/lib/utils';

export function Incidents() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [showOpen, setShowOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await api.listIncidents({ open: showOpen });
      setIncidents(res.incidents);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [showOpen]);

  async function ack(id: string) {
    await api.ackIncident(id);
    load();
  }
  async function resolve(id: string) {
    if (!confirm('Force-resolve this incident?')) return;
    await api.resolveIncident(id);
    load();
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Incidents</h1>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showOpen}
            onChange={(e) => setShowOpen(e.target.checked)}
          />
          Open only
        </label>
      </div>

      {error && <Card className="p-4 border-destructive/50 text-destructive">{error}</Card>}

      {!incidents.length && (
        <Card className="p-12 text-center text-muted-foreground">
          No incidents {showOpen ? 'open' : 'recorded'}.
        </Card>
      )}

      <div className="grid gap-3">
        {incidents.map((i) => {
          const open = !i.resolved_at;
          const duration = open
            ? Math.floor((Date.now() / 1000 - i.opened_at) / 60)
            : Math.floor((i.resolved_at! - i.opened_at) / 60);
          return (
            <Card key={i.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant={open ? 'destructive' : 'success'}>
                      {open ? 'OPEN' : 'RESOLVED'}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {i.id.slice(-10)}
                    </span>
                    {i.acknowledged_by && (
                      <Badge variant="secondary" className="text-[10px]">
                        ACK · {i.acknowledged_by}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm font-medium">{i.cause || 'Unknown cause'}</div>
                  {i.last_error && i.last_error !== i.cause && (
                    <div className="text-xs text-muted-foreground">
                      Last error: {i.last_error}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    Opened {formatRelative(i.opened_at * 1000)} · Duration{' '}
                    {duration < 60 ? `${duration}m` : `${Math.floor(duration / 60)}h ${duration % 60}m`}
                    {' · '}
                    {i.failure_count} failed check{i.failure_count === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {open && !i.acknowledged_by && (
                    <Button size="sm" variant="outline" onClick={() => ack(i.id)}>
                      Ack
                    </Button>
                  )}
                  {open && (
                    <Button size="sm" variant="destructive" onClick={() => resolve(i.id)}>
                      Resolve
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
